import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';

import { cn } from '@/lib/utils';

export interface SegmentedToggleOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedToggleProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  /** 2択の選択肢。並び順がそのまま左右の並びになる */
  options: readonly [SegmentedToggleOption<T>, SegmentedToggleOption<T>];
  /** ラベル要素の id（`<Label id="...">` と対にする） */
  labelledBy?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * 2択の切り替えUI。ピル状の枠の中でつまみがスライドする。
 *
 * ラジオボタンと違い両方の選択肢が常に見えるため、
 * 「いま何が選ばれていて、もう一方が何か」が一目で分かる。
 */
export function SegmentedToggle<T extends string>({
  value,
  onValueChange,
  options,
  labelledBy,
  disabled = false,
  className,
}: SegmentedToggleProps<T>) {
  const selectedIndex = options.findIndex((option) => option.value === value);

  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      // Radix は選択中の項目を再クリックすると空文字を返す。2択では非選択状態を持たないため無視する
      onValueChange={(next) => {
        if (next) onValueChange(next as T);
      }}
      aria-labelledby={labelledBy}
      disabled={disabled}
      className={cn(
        'relative inline-grid grid-cols-2 gap-0 rounded-full bg-gray-200 p-1',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-white shadow transition-transform duration-200"
        style={{ transform: `translateX(${selectedIndex > 0 ? '100%' : '0%'})` }}
      />
      {options.map((option) => (
        <ToggleGroupPrimitive.Item
          key={option.value}
          value={option.value}
          // 表示文字と同じ内容だが、支援技術とテストが項目を名前で引けるようにする
          aria-label={option.label}
          className={cn(
            'relative z-10 min-w-20 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed',
            'data-[state=on]:text-gray-900 data-[state=off]:text-gray-500',
            !disabled && 'data-[state=off]:hover:text-gray-700',
          )}
        >
          {option.label}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}
